import React, { useState, useEffect, useRef } from "react";
import { Form, Button, Spinner } from "react-bootstrap";
import { useDispatch, useSelector } from "react-redux";
import { addErrorAlert } from "../../../alert/Actions";
import { UPLOAD_FILES_LENGTH } from "../../../../shared/constants/Constants";
import { loadTicketAttachmentAddDataRequest } from "../redux/Actions";
import "../../ticketcreation/components/TicketCreation.css";

const ALLOWED_EXTENSIONS = [
  "png", "jpg", "jpeg", "gif",
  "pdf", "xlsx", "doc", "docx", "xls", "mp4",
];
const MAX_FILE_SIZE = 10 * 1024 * 1024; // 10 MB

const TicketAttachmentUpload = ({ ticketId }) => {
  const dispatch = useDispatch();

  const [uploadingFiles, setUploadingFiles] = useState([]);
  const [failedFiles, setFailedFiles] = useState([]);

  const existingAttachments = useSelector(
    (store) => store.ticketing.ticketdetails?.attachments || []
  );
  const prevAttachmentCountRef = useRef(existingAttachments.length);

  useEffect(() => {
    if (existingAttachments.length > prevAttachmentCountRef.current) {
      const newlyAdded = existingAttachments.slice(prevAttachmentCountRef.current);
      setUploadingFiles((prev) =>
        prev.filter((p) => !newlyAdded.some((att) => att.file_name?.endsWith(p.name)))
      );
    }
    prevAttachmentCountRef.current = existingAttachments.length;
  }, [existingAttachments]);

  const uploadFile = (file) => {
    const tempId = `${file.name}-${file.size}-${Date.now()}`;

    setUploadingFiles((prev) => [...prev, { tempId, name: file.name, size: file.size }]);

    // Same localStorage pattern as TicketDetails.js's send-message flow
    const emp_no = JSON.parse(localStorage.getItem("emp_no"));
    const employee = JSON.parse(localStorage.getItem("employee"));
    const emp_name = employee
      ? [employee.first_name, employee.last_name, emp_no].filter(Boolean).join(", ")
      : "";

    const formData = new FormData();
    formData.append("attachments", file);
    formData.append("ticket_id", ticketId);
    formData.append("emp_id", emp_no);
    formData.append("employee_id", employee?.id);
    formData.append("emp_name", emp_name);

    dispatch(loadTicketAttachmentAddDataRequest(formData));

    setTimeout(() => {
      setUploadingFiles((prev) => {
        const stillPending = prev.find((p) => p.tempId === tempId);
        if (stillPending) setFailedFiles((f) => [...f, tempId]);
        return prev;
      });
    }, 15000);
  };

  const handleFileUpload = (selectedFiles) => {
    if (selectedFiles.length > UPLOAD_FILES_LENGTH) {
      dispatch(addErrorAlert(`You can upload max ${UPLOAD_FILES_LENGTH} files at a time.`));
      return;
    }

    for (let file of selectedFiles) {
      const ext = file.name.split(".").pop().toLowerCase();

      if (!ALLOWED_EXTENSIONS.includes(ext)) {
        dispatch(addErrorAlert(`Invalid file type: ${file.name}`));
        continue;
      }
      if (file.size > MAX_FILE_SIZE) {
        dispatch(addErrorAlert(`File too large: ${file.name}. Max size is 10MB.`));
        continue;
      }
      uploadFile(file);
    }
  };

  const retryUpload = (tempId, file) => {
    setFailedFiles((prev) => prev.filter((id) => id !== tempId));
    setUploadingFiles((prev) => prev.filter((p) => p.tempId !== tempId));
    uploadFile(file);
  };

  // Removes the card from the UI. Note: if the file already fired its
  // request, this doesn't cancel the in-flight upload — it just stops
  // tracking it locally. The result (success/fail) will be ignored,
  // but if it succeeds, it'll still land in ticketdetails.attachments.
  const removeFromList = (tempId) => {
    setUploadingFiles((prev) => prev.filter((p) => p.tempId !== tempId));
    setFailedFiles((prev) => prev.filter((id) => id !== tempId));
  };

  return (
    <div className="mb-2">
      <div className="d-flex justify-content-between align-items-center mb-2">
        <Form.Label className="fw-bold mb-0">Add Attachments</Form.Label>
        <Button
          type="button"
          className="btn default-btn-color"
          onClick={() => document.getElementById("ticketDetailsFileUpload").click()}
        >
          Choose Files
        </Button>
      </div>

      <input
        id="ticketDetailsFileUpload"
        type="file"
        multiple
        accept=".png,.jpg,.jpeg,.gif,.pdf,.xlsx,.doc,.docx,.xls,.mp4"
        style={{ display: "none" }}
        onChange={(e) => {
          handleFileUpload(Array.from(e.target.files));
          e.target.value = "";
        }}
      />

      <div
        className="dropzone"
        onDragOver={(e) => { e.preventDefault(); e.stopPropagation(); }}
        onDrop={(e) => {
          e.preventDefault();
          e.stopPropagation();
          handleFileUpload(Array.from(e.dataTransfer.files));
        }}
      >
        {uploadingFiles.length === 0 && (
          <div className="text-center text-muted">
            <p className="mb-1">Drag & drop files here</p>
            <p className="mb-0">or click "Choose Files" — files upload automatically</p>
          </div>
        )}

        {uploadingFiles.length > 0 && (
          <div className="attachments-container">
            {uploadingFiles.map((file) => {
              const failed = failedFiles.includes(file.tempId);
              return (
                <div className="attachment-card" key={file.tempId}>
                  <div className="attachment-info">
                    <span className="file-name">{file.name}</span>
                    <span className="file-size">
                      {(file.size / 1024 / 1024).toFixed(2)} MB
                    </span>
                  </div>

                  <div className="d-flex align-items-center gap-2">
                    {failed ? (
                      <span
                        className="text-danger"
                        style={{ cursor: "pointer", fontSize: "12px" }}
                        onClick={() => retryUpload(file.tempId, file)}
                      >
                        Failed — Retry
                      </span>
                    ) : (
                      <Spinner animation="border" size="sm" />
                    )}

                    <span
                      className="delete-icon"
                      onClick={() => removeFromList(file.tempId)}
                      title="Remove"
                      style={{ cursor: "pointer" }}
                    >
                      <i className="fa fa-trash"></i>
                    </span>
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default TicketAttachmentUpload;